import apiClient, { type ApiError } from './apiClient'
import { API_ENDPOINTS } from './config'
import { isAuthError, createAuthError, extractErrorMessage } from '@/lib/utils/errorHandling'
import { showError } from '@/lib/utils/notifications'
import type { NinjaIbvSessionStepPayload } from './bankService.types'

export type { NinjaIbvSessionStepPayload } from './bankService.types'

/**
 * Bank Linking Service
 * Handles all bank account linking operations for Plaid and Ninja integrations
 */

// ==================== Types ====================

export interface PlaidLinkTokenResponse {
  link_token: string
}

export interface PlaidAccount {
  pli_id: number
  ins_name: string
  subtype: string
  mask: string
  account_id: string
}

export interface NinjaConfig {
  NINJA_JS_URL?: string
  NINJA_JS_URL_V2?: string
  NINJA_VERSION?: 'V1' | 'V2' | string
  NINJA_REQUIRED: 'YES' | 'NO'
  NINJA_API_URL?: string
  NINJA_API_KEY?: string
}

export interface NinjaLinkTokenResponse {
  link_token: string
}

export interface NinjaIbvRequestResponse {
  ibv_connection_link?: string
  product_request_id?: string
  [key: string]: unknown
}

export interface NinjaAccount {
  pli_id: number
  bank_name: string
  ibv_report_status: string
  account_type: string
  mask: string
  /** Ninja / IBV “public token” used with POST /api/ninja/link — often same as product_request_id. */
  product_request_id?: string
  request_id?: string
  public_token?: string
  account_name?: string
  user_retry_count?: number
}

/** Heuristic: Ninja product request ids used in IBV session logs (e.g. `IBVBE24824476E5BAB0E792`). */
export function looksLikeNinjaIbProductRequestId(value: unknown): boolean {
  if (typeof value !== "string") return false;
  const t = value.trim();
  return t.length >= 12 && /^IBV[A-Z0-9_-]+$/i.test(t);
}

function normalizeNinjaAccountFromApi(raw: unknown): NinjaAccount {
  const row =
    typeof raw === "object" && raw !== null
      ? (raw as Record<string, unknown>)
      : {};
  const base = { ...row } as unknown as NinjaAccount;
  const explicitKeys = [
    "product_request_id",
    "productRequestId",
    "ninja_product_request_id",
    "ibv_product_request_id",
  ];
  let explicit: string | undefined;
  for (const k of explicitKeys) {
    const v = row[k];
    if (typeof v === "string" && v.trim() !== "") {
      explicit = v.trim();
      break;
    }
  }
  const inferKeys = [
    "public_token",
    "request_id",
    "ninja_request_id",
    "session_product_request_id",
    "ibv_session_id",
  ];
  let inferred: string | undefined;
  for (const k of inferKeys) {
    const v = row[k];
    if (typeof v === "string" && looksLikeNinjaIbProductRequestId(v)) {
      inferred = v.trim();
      break;
    }
  }
  const product_request_id = explicit ?? inferred;
  if (product_request_id) {
    return { ...base, product_request_id };
  }
  return base;
}

export interface LinkAccountRequest {
  public_token: string
}

export interface OptOutRequest {
  plaid_opt_out?: '1' | '0'
  ninja_opt_out?: '1' | '0'
}

export interface NoBankUploadRequest {
  bank_name: string
  ninja_income_proof: File | File[]
}

export interface ApiSuccessResponse {
  success: boolean
  message: string
}

// ==================== Plaid API ====================

/**
 * Create Plaid link token
 * @returns Link token for Plaid Link initialization
 */
export async function createPlaidLinkToken(): Promise<string> {
  try {
    const response = await apiClient.post<PlaidLinkTokenResponse>(
      API_ENDPOINTS.PLAID_CREATE_LINK_TOKEN
    )
    return response.data.link_token
  } catch (error) {
    if (isAuthError(error)) {
      throw createAuthError()
    }
    throw {
      message: extractErrorMessage(error, 'Failed to create Plaid link token.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Link Plaid account to applicant
 * @param applicantId - Applicant ID
 * @param publicToken - Public token from Plaid Link
 */
export async function linkPlaidAccount(
  applicantId: string | number,
  publicToken: string
): Promise<ApiSuccessResponse> {
  try {
    const response = await apiClient.post<ApiSuccessResponse>(
      API_ENDPOINTS.PLAID_LINK_ACCOUNT(applicantId),
      { public_token: publicToken }
    )
    return response.data
  } catch (error) {
    if (isAuthError(error)) {
      throw createAuthError()
    }
    throw {
      message: extractErrorMessage(error, 'Failed to link Plaid account.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * List all linked Plaid accounts for an applicant
 * @param applicantId - Applicant ID
 * @returns Array of linked Plaid accounts
 */
export async function listPlaidAccounts(
  applicantId: string | number
): Promise<PlaidAccount[]> {
  try {
    const response = await apiClient.get<PlaidAccount[]>(
      API_ENDPOINTS.PLAID_LIST_ACCOUNTS(applicantId)
    )
    return Array.isArray(response.data) ? response.data : []
  } catch (error) {
    if (isAuthError(error)) {
      throw createAuthError()
    }
    throw {
      message: extractErrorMessage(error, 'Failed to fetch Plaid accounts.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Delete a linked Plaid account
 * @param applicantId - Applicant ID
 * @param pliId - Plaid Link ID
 */
export async function deletePlaidAccount(
  applicantId: string | number,
  pliId: string | number
): Promise<ApiSuccessResponse> {
  try {
    const response = await apiClient.delete<ApiSuccessResponse>(
      API_ENDPOINTS.PLAID_DELETE_ACCOUNT(applicantId, pliId)
    )
    return response.data
  } catch (error) {
    if (isAuthError(error)) {
      throw createAuthError()
    }
    throw {
      message: extractErrorMessage(error, 'Failed to delete Plaid account.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Opt out of Plaid bank linking
 * @param applicantId - Applicant ID
 */
export async function optOutPlaid(
  applicantId: string | number
): Promise<ApiSuccessResponse> {
  try {
    const response = await apiClient.post<ApiSuccessResponse>(
      API_ENDPOINTS.PLAID_OPT_OUT(applicantId),
      { plaid_opt_out: '1' }
    )
    return response.data
  } catch (error) {
    if (isAuthError(error)) {
      throw createAuthError()
    }
    throw {
      message: extractErrorMessage(error, 'Failed to opt out of Plaid.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

// ==================== Ninja API ====================

/**
 * Get Ninja configuration
 * @returns Ninja configuration including JS URL and requirements
 */
export async function getNinjaConfig(): Promise<NinjaConfig> {
  try {
    const response = await apiClient.get<NinjaConfig>(
      API_ENDPOINTS.NINJA_CONFIG
    )
    return response.data
  } catch (error) {
    if (isAuthError(error)) {
      throw createAuthError()
    }
    throw {
      message: extractErrorMessage(error, 'Failed to fetch Ninja configuration.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Create Ninja link token
 * @param applicantId - Applicant ID
 * @returns Link token for Ninja widget initialization
 */
export async function createNinjaLinkToken(
  applicantId: string | number
): Promise<string> {
  try {
    // Note: Backend currently generates tokens with edge_test_all_features format
    // Once backend is updated to use LR format, we can pass consumer_id here
    const response = await apiClient.post<NinjaLinkTokenResponse>(
      API_ENDPOINTS.NINJA_CREATE_LINK_TOKEN,
      { applicant_id: applicantId }
    )
    return response.data.link_token
  } catch (error) {
    if (isAuthError(error)) {
      throw createAuthError()
    }
    const apiError = error as ApiError & { data?: unknown; errors?: Record<string, string[]> }
    throw {
      message: extractErrorMessage(error, 'Failed to create Ninja link token.'),
      status: apiError.status,
      data: apiError.data,
      errors: apiError.errors,
    } as ApiError & { data?: unknown }
  }
}

/**
 * Create an IBV request and get the connection iframe link.
 * Mirrors Angular flow: POST /api/ninja/ibv_request/:applicantId
 */
export async function createNinjaIbvRequest(
  applicantId: string | number
): Promise<NinjaIbvRequestResponse> {
  try {
    const response = await apiClient.post<NinjaIbvRequestResponse>(
      API_ENDPOINTS.NINJA_IBV_REQUEST(applicantId),
    )
    return response.data
  } catch (error) {
    if (isAuthError(error)) {
      throw createAuthError()
    }
    const apiError = error as ApiError & { data?: unknown; errors?: Record<string, string[]> }
    throw {
      message: extractErrorMessage(error, 'Failed to create Ninja IBV request.'),
      status: apiError.status,
      data: apiError.data,
      errors: apiError.errors,
    } as ApiError & { data?: unknown }
  }
}

/**
 * Serializes IBV session-step POSTs so the next call waits for the previous one
 * to finish. Prevents concurrent iframe notifications from racing on BE events_json.
 */
let ibvSessionStepQueue: Promise<void> = Promise.resolve()

/**
 * Persist one Ninja IBV iframe step (popup open/close + iframe notifications).
 * Requests are queued: each POST starts only after the previous attempt settles.
 * Errors are shown but do not throw (and do not block later steps), so bank linking can continue.
 */
export async function reportNinjaIbvSessionStep(
  applicantId: string | number,
  payload: NinjaIbvSessionStepPayload
): Promise<void> {
  if (!payload.product_request_id) return

  const run = async () => {
    try {
      await apiClient.post(API_ENDPOINTS.NINJA_IBV_SESSION_STEPS(applicantId), payload)
    } catch (error) {
      showError(extractErrorMessage(error, 'Unable to record bank verification step'))
    }
  }

  // Always continue the chain after settle so one failed step does not drop later events.
  const queued = ibvSessionStepQueue.then(run, run)
  ibvSessionStepQueue = queued.then(
    () => undefined,
    () => undefined,
  )
  return queued
}

/**
 * Fetch stored bank-linking product requests + step events (Angular View Logs).
 */
export async function getNinjaApplicantAccountRequests(
  applicantId: string | number
): Promise<unknown[]> {
  try {
    const response = await apiClient.get<unknown[]>(
      API_ENDPOINTS.NINJA_APPLICANT_ACCOUNT_REQUESTS(applicantId)
    )
    return Array.isArray(response.data) ? response.data : []
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to load bank linking logs.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Link Ninja account to applicant
 * @param applicantId - Applicant ID
 * @param publicToken - Product request ID or request ID from Ninja widget
 */
export async function linkNinjaAccount(
  applicantId: string | number,
  publicToken: string
): Promise<ApiSuccessResponse> {
  try {
    const response = await apiClient.post<ApiSuccessResponse>(
      API_ENDPOINTS.NINJA_LINK_ACCOUNT(applicantId),
      { public_token: publicToken }
    )
    return response.data
  } catch (error) {
    if (isAuthError(error)) {
      throw createAuthError()
    }
    throw {
      message: extractErrorMessage(error, 'Failed to link Ninja account.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * List all linked Ninja accounts for an applicant
 * @param applicantId - Applicant ID
 * @returns Array of linked Ninja accounts
 */
export async function listNinjaAccounts(
  applicantId: string | number
): Promise<NinjaAccount[]> {
  try {
    const response = await apiClient.get<unknown[]>(
      API_ENDPOINTS.NINJA_LIST_ACCOUNTS(applicantId)
    )
    const raw = Array.isArray(response.data) ? response.data : []
    return raw.map((row) => normalizeNinjaAccountFromApi(row))
  } catch (error) {
    if (isAuthError(error)) {
      throw createAuthError()
    }
    throw {
      message: extractErrorMessage(error, 'Failed to fetch Ninja accounts.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Delete a linked Ninja account
 * @param applicantId - Applicant ID
 * @param pliId - Account Link ID
 */
export async function deleteNinjaAccount(
  applicantId: string | number,
  pliId: string | number
): Promise<ApiSuccessResponse> {
  try {
    const response = await apiClient.delete<ApiSuccessResponse>(
      API_ENDPOINTS.NINJA_DELETE_ACCOUNT(applicantId, pliId)
    )
    return response.data
  } catch (error) {
    if (isAuthError(error)) {
      throw createAuthError()
    }
    throw {
      message: extractErrorMessage(error, 'Failed to delete Ninja account.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Opt out of Ninja bank linking
 * @param applicantId - Applicant ID
 */
export async function optOutNinja(
  applicantId: string | number
): Promise<ApiSuccessResponse> {
  try {
    const response = await apiClient.post<ApiSuccessResponse>(
      API_ENDPOINTS.NINJA_OPT_OUT(applicantId),
      { ninja_opt_out: '1' }
    )
    return response.data
  } catch (error) {
    if (isAuthError(error)) {
      throw createAuthError()
    }
    throw {
      message: extractErrorMessage(error, 'Failed to opt out of Ninja.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Upload bank statement or proof of income when bank cannot be linked.
 * Single file: one request with ninja_income_proof. Multiple files: one request
 * per file so the backend receives each file with the same shape (backend must
 * append to the list). Content-Type is left unset so the browser sets
 * multipart/form-data with boundary.
 */
export async function uploadNoBankNinja(
  applicantId: string | number,
  bankName: string,
  files: File | File[]
): Promise<ApiSuccessResponse> {
  const fileArray = Array.isArray(files) ? files : [files]
  const url = API_ENDPOINTS.NINJA_NO_BANK_UPLOAD(applicantId)

  const uploadOne = async (file: File): Promise<ApiSuccessResponse> => {
    const formData = new FormData()
    formData.append('bank_name', bankName)
    formData.append('ninja_income_proof', file)
    const response = await apiClient.post<ApiSuccessResponse>(url, formData, {
      // Do not set Content-Type; browser sets multipart/form-data with boundary
    })
    return response.data
  }

  try {
    if (fileArray.length === 1) {
      return await uploadOne(fileArray[0])
    }
    for (const file of fileArray) {
      await uploadOne(file)
    }
    return { success: true, message: 'Documents uploaded.' }
  } catch (error) {
    if (isAuthError(error)) {
      throw createAuthError()
    }
    throw {
      message: extractErrorMessage(error, 'Failed to upload bank documents.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}


/**
 * Soft-refresh / resubmit the Ninja IBV income report.
 * Mirrors Angular `refreshIbvNinjaReport()` → POST /api/ninja/ibv_soft_refresh/:id
 */
export async function softRefreshNinjaIbvReport(
  applicantId: string | number
): Promise<{ message?: string }> {
  try {
    const response = await apiClient.post<{ message?: string }>(
      API_ENDPOINTS.NINJA_IBV_SOFT_REFRESH(applicantId),
      {}
    )
    return response.data ?? {}
  } catch (error) {
    if (isAuthError(error)) {
      throw createAuthError()
    }
    throw {
      message: extractErrorMessage(error, 'Failed to resubmit Ninja report.'),
      status: (error as ApiError).status,
      data: (error as ApiError & { data?: unknown }).data,
    } as ApiError & { data?: unknown }
  }
}


